import { type FormEvent, useEffect, useMemo, useState } from 'react';
import zxcvbn from 'zxcvbn';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { AuthLayout } from '../components/AuthLayout';
import { PasswordField } from '../components/PasswordField';
import { clearAuthFeedback, requestPasswordRecovery, resetPassword } from '../store/authSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';

const resetErrors: Record<string, string> = {
  ERR_PASSWORD_TOO_WEAK: 'Das Passwort ist zu schwach.',
  ERR_PASSWORD_TOO_SHORT: 'Das Passwort ist zu kurz (mindestens 12 Zeichen).',
  ERR_PASSWORD_REPEATED: 'Das Passwort kann nicht wiederverwendet werden. Wählen Sie ein neues Passwort.'
};

function Actions({ busy, disabled }: { busy: boolean; disabled: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, pt: 1 }}>
      <Link href="/login" underline="hover">Zurück</Link>
      <Button type="submit" variant="contained" disabled={disabled || busy} sx={{ width: 168 }}>
        {busy ? 'Verbinde …' : 'Passwort zurücksetzen'}
      </Button>
    </Box>
  );
}

function RequestRecovery() {
  const dispatch = useAppDispatch();
  const { recoveryStatus, recoveryEmail, error } = useAppSelector((state) => state.auth);
  const [email, setEmail] = useState('');

  if (recoveryStatus === 'success') {
    return (
      <Stack spacing={2}>
        <CheckCircleOutlineRoundedIcon color="success" sx={{ fontSize: 38 }} />
        <Typography component="h1" variant="h2">E-Mail gesendet</Typography>
        <Typography color="text.secondary">
          Wir haben eine E-Mail mit einem Link zum Zurücksetzen des Passworts an {recoveryEmail} gesendet.
        </Typography>
        <Typography variant="body2">
          Prüfen Sie bitte auch den Spam-Ordner. Kontrollieren Sie außerdem, ob die eingegebene Adresse korrekt ist.
        </Typography>
        <Link href="/login" underline="hover">Zurück zur Anmeldung</Link>
      </Stack>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void dispatch(requestPasswordRecovery(email));
  };

  return (
    <>
      <Typography component="h1" variant="h2">Passwort vergessen?</Typography>
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        Geben Sie die E-Mail-Adresse ein, mit der Sie sich angemeldet haben. Wir senden Ihnen eine Anleitung zum Zurücksetzen des Passworts.
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Stack component="form" onSubmit={submit} spacing={2} sx={{ mt: 2.5 }}>
        <TextField
          label="E-Mail"
          name="recovery-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoFocus
          autoComplete="email"
          error={Boolean(error)}
        />
        <Actions busy={recoveryStatus === 'submitting'} disabled={!email} />
      </Stack>
    </>
  );
}

function ResetPassword({ token }: { token: string }) {
  const dispatch = useAppDispatch();
  const { resetStatus, error } = useAppSelector((state) => state.auth);
  const [password, setPassword] = useState('');
  const passwordError = useMemo(() => {
    if (!password) return '';
    if (password.length < 12) return 'Das Passwort muss mindestens 12 Zeichen lang sein.';
    if (zxcvbn(password).score < 3) return 'Das Passwort ist zu schwach.';
    return '';
  }, [password]);

  useEffect(() => {
    if (resetStatus === 'success') window.location.assign('/login?reset=success');
  }, [resetStatus]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!passwordError && password) void dispatch(resetPassword({ token, password }));
  };

  return (
    <>
      <Typography component="h1" variant="h2">Passwort zurücksetzen</Typography>
      <Typography color="text.secondary" sx={{ mt: 2 }}>Vergeben Sie ein neues Passwort für das Konto.</Typography>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{resetErrors[error] || 'Es ist leider ein unbekannter Fehler aufgetreten.'}</Alert>}
      <Stack component="form" onSubmit={submit} spacing={1.5} sx={{ mt: 2.5 }}>
        <PasswordField
          label="Neues Passwort"
          name="recovery-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          autoFocus
          autoComplete="new-password"
          error={Boolean(passwordError || error)}
          helperText={passwordError || 'Mindestens 12 Zeichen; verwenden Sie ein noch nie genutztes Passwort.'}
        />
        <Actions busy={resetStatus === 'submitting'} disabled={!password || Boolean(passwordError)} />
      </Stack>
    </>
  );
}

export function RecoveryPage() {
  const dispatch = useAppDispatch();
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => () => { dispatch(clearAuthFeedback()); }, [dispatch]);

  return <AuthLayout>{token ? <ResetPassword token={token} /> : <RequestRecovery />}</AuthLayout>;
}
